<template>
    <div class="relative">
        <div
            ref="minimapContainer"
            class="fixed right-6 bottom-4 overflow-hidden rounded-md bg-white shadow-md"
            :style="minimapContainerStyle"
            @mouseleave="handleDragEnd"
        >
            <iframe
                id="iframe-minimap"
                ref="minimapIframe"
                @load="handleMinimapLoad"
                class="origin-top-left"
                :src="src"
                width="100%"
                height="100%"
                frameborder="0"
            />
            <div
                ref="scrollIndicator"
                class="absolute top-0 left-0 w-full cursor-grab hover:bg-black/30 z-20"
                :class="{ '!cursor-grabbing': isDragging }"
                :style="[
                    viewportIndicatorStyle,
                    {
                        borderRadius: `${6 / minimapScale}px`,
                        border: `${2 / minimapScale}px solid black`,
                    },
                ]"
                @mousedown="handleDragStart"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useActions } from "@/store/vuex-composition-mappers";
import { usePagePreviewStore } from "@/apps/page/store/page-preview.store";

//@ts-expect-error BF: fix types
const { setIframeMinimapLoaded } = useActions("page", ["setIframeMinimapLoaded"]);

defineProps<{ src: string }>();

const minimapContainer = ref<HTMLElement | null>(null);
const minimapIframe = ref<HTMLIFrameElement | null>(null);
const editorIframe = ref<HTMLIFrameElement | null>(null);
const scrollIndicator = ref<HTMLElement | null>(null);

const editorIframeScrollPercent = ref(0);

const dimensions = ref({
    editor: {
        width: 0,
        clientHeight: 0,
        scrollHeight: 0,
    },
    minimap: {
        scrollHeight: 0,
        clientHeight: 0,
    },
});

const isDragging = ref(false);
const dragStartY = ref(0);
const dragStartScrollPercent = ref(0);

const pagePreviewStore = usePagePreviewStore();

const baseMinimapHeight = 400;
const baseMinimapWidth = computed(() => {
    if (pagePreviewStore.viewport === "mobile") return 60;
    return 120;
});

const minimapScale = computed(() => {
    if (dimensions.value.editor.width === 0) return 0;
    return baseMinimapWidth.value / dimensions.value.editor.width;
});

const minimapHeight = computed(() => {
    const maxHeight = baseMinimapHeight / minimapScale.value;

    return Math.min(dimensions.value.editor.scrollHeight || maxHeight, maxHeight);
});

const minimapContainerStyle = computed(() => ({
    width: `${dimensions.value.editor.width}px`,
    transform: `scale(${minimapScale.value})`,
    transformOrigin: "right bottom",
    height: `${minimapHeight.value}px`,
    borderRadius: `${6 / minimapScale.value}px`,
    outline: `${2 / minimapScale.value}px solid lightgray`,
}));

const viewportIndicatorStyle = computed(() => {
    const { editor, minimap } = dimensions.value;
    const indicatorHeightPercentage = editor.clientHeight / editor.scrollHeight;
    const indicatorHeight = minimap.scrollHeight * indicatorHeightPercentage;

    const maximumTranslateY = minimapHeight.value - indicatorHeight;
    const translateY = maximumTranslateY * editorIframeScrollPercent.value;

    return {
        height: `${indicatorHeight}px`,
        transform: `translateY(${translateY}px)`,
        opacity: isDragging.value ? "0.8" : "0.6",
    };
});

const updateDimensions = () => {
    if (
        !editorIframe.value?.contentWindow?.document.documentElement ||
        !minimapIframe.value?.contentWindow?.document.documentElement
    )
        return;

    const editorDoc = editorIframe.value.contentWindow.document.documentElement;
    const minimapDoc = minimapIframe.value.contentWindow.document.documentElement;

    dimensions.value = {
        editor: {
            width: editorIframe.value.offsetWidth,
            clientHeight: editorDoc.clientHeight,
            scrollHeight: editorDoc.scrollHeight,
        },
        minimap: {
            scrollHeight: minimapDoc.scrollHeight,
            clientHeight: minimapDoc.clientHeight,
        },
    };
};

const updateScrollPosition = () => {
    const editorDoc = editorIframe.value?.contentWindow?.document.documentElement;
    if (!editorDoc) return;

    const scrollPosition = editorDoc.scrollTop;
    const maxScroll = editorDoc.scrollHeight - editorDoc.clientHeight;

    // Only update if not dragging to prevent conflicts
    if (!isDragging.value) {
        editorIframeScrollPercent.value = maxScroll > 0 ? scrollPosition / maxScroll : 0;
    }

    syncMinimapScroll();
};

const syncMinimapScroll = () => {
    const minimapDoc = minimapIframe.value?.contentWindow?.document.documentElement;
    if (!minimapDoc) return;

    const maxScroll = minimapDoc.scrollHeight - minimapDoc.clientHeight;
    minimapDoc.scrollTo(0, maxScroll * editorIframeScrollPercent.value);
};

const handleMinimapClick = (event: MouseEvent) => {
    const minimapDoc = minimapIframe.value?.contentWindow?.document.documentElement;
    const editorDoc = editorIframe.value?.contentWindow?.document.documentElement;

    if (!minimapDoc || !editorDoc) return;

    editorDoc.style.scrollBehavior = "auto";

    const clickY = event.clientY + minimapDoc.scrollTop;
    const relativeClickPosition = clickY / minimapDoc.scrollHeight;
    const targetScrollPosition =
        (editorDoc.scrollHeight - editorDoc.clientHeight) * relativeClickPosition;

    editorDoc.scrollTo({ top: targetScrollPosition });

    setTimeout(() => {
        editorDoc.style.removeProperty("scrollBehavior");
    }, 100);
};

const setupMinimap = () => {
    const minimapDoc = minimapIframe.value?.contentWindow?.document;
    if (!minimapDoc) return;

    minimapDoc.addEventListener("click", handleMinimapClick);

    // Set overflow hidden to hide scroll on minimap
    const style = minimapDoc.createElement("style");
    style.textContent = "body { overflow: hidden; }";
    minimapDoc.head.appendChild(style);
    minimapDoc.documentElement.style.scrollBehavior = "auto";

    const observer = new MutationObserver(() => {
        updateDimensions();
        updateScrollPosition();
    });
    observer.observe(minimapDoc.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
    });

    // Add load event listeners to all images and iframes
    const mediaElements = minimapDoc.querySelectorAll("img, iframe");
    mediaElements.forEach((element) => {
        element.addEventListener("load", updateDimensions);
    });

    // Use requestAnimationFrame to check dimensions multiple times
    // to make sure the iframe content is fully loaded
    let frameCount = 0;
    const checkDimensions = () => {
        updateDimensions();
        frameCount++;
        if (frameCount < 10) {
            requestAnimationFrame(checkDimensions);
        }
    };
    requestAnimationFrame(checkDimensions);

    return observer;
};

const handleMinimapLoad = async () => {
    setIframeMinimapLoaded();
    const observer = setupMinimap();
    updateDimensions();

    if (observer) {
        onUnmounted(() => observer.disconnect());
    }
};

function handleDragStart(e: MouseEvent) {
    if (!minimapContainer.value || !editorIframe.value?.contentWindow?.document.documentElement)
        return;

    isDragging.value = true;
    dragStartY.value = e.clientY;
    dragStartScrollPercent.value = editorIframeScrollPercent.value;

    editorIframe.value.contentWindow.document.documentElement.style.scrollBehavior = "auto";

    window.addEventListener("mousemove", handleDrag);
    window.addEventListener("mouseup", handleDragEnd, { once: true });

    e.preventDefault();
}

function handleDrag(e: MouseEvent) {
    if (
        !isDragging.value ||
        !minimapContainer.value ||
        !editorIframe.value?.contentWindow?.document.documentElement
    )
        return;

    const containerRect = minimapContainer.value.getBoundingClientRect();

    // Check if mouse is outside the container bounds
    if (
        e.clientX < containerRect.left ||
        e.clientX > containerRect.right ||
        e.clientY < containerRect.top ||
        e.clientY > containerRect.bottom
    ) {
        handleDragEnd();
        return;
    }

    // Calculate the delta movement considering the scale
    const deltaY = (e.clientY - dragStartY.value) / minimapScale.value;

    // Adjust this value to make movement faster/slower
    const speedMultiplier = 1.5;

    // Calculate the scroll range and convert delta to percentage
    const { editor } = dimensions.value;
    const scrollRange = editor.scrollHeight - editor.clientHeight;
    const deltaPercent = (deltaY * speedMultiplier) / minimapHeight.value;

    // Calculate new scroll percentage
    const newScrollPercent = Math.max(0, Math.min(1, dragStartScrollPercent.value + deltaPercent));

    // Update the scroll percentage state - this will trigger viewport indicator update
    editorIframeScrollPercent.value = newScrollPercent;

    // Update editor scroll position
    const editorDoc = editorIframe.value.contentWindow.document.documentElement;
    const targetScrollPosition = scrollRange * newScrollPercent;
    editorDoc.scrollTop = targetScrollPosition;
}

function handleDragEnd() {
    if (!isDragging.value) return;

    isDragging.value = false;
    window.removeEventListener("mousemove", handleDrag);

    if (editorIframe.value?.contentWindow?.document.documentElement) {
        editorIframe.value.contentWindow.document.documentElement.style.removeProperty(
            "scrollBehavior"
        );
    }
}

function updateMinimap() {
    updateDimensions();
    updateScrollPosition();
}

onMounted(() => {
    editorIframe.value = document.getElementById("iframe") as HTMLIFrameElement;

    if (!editorIframe.value?.contentWindow) return;

    watch(
        () => [pagePreviewStore.viewport, pagePreviewStore.scale],
        () => {
            setTimeout(updateMinimap, 100);
        }
    );

    editorIframe.value.contentWindow.addEventListener("scroll", updateMinimap);
    editorIframe.value.contentWindow.addEventListener("resize", updateMinimap);

    onUnmounted(() => {
        editorIframe.value?.contentWindow?.removeEventListener("scroll", updateMinimap);
        editorIframe.value?.contentWindow?.removeEventListener("resize", updateMinimap);
        minimapIframe.value?.contentWindow?.document.removeEventListener(
            "click",
            handleMinimapClick
        );
        window.removeEventListener("mousemove", handleDrag);
        window.removeEventListener("mouseup", handleDragEnd);
    });
});
</script>
